import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../Card';
import { 
  Printer, 
  Upload, 
  FileText, 
  Settings, 
  CheckCircle, 
  AlertCircle,
  Info,
  Zap,
  Monitor,
  Server,
  Trash2,
  Eye,
  AlertTriangle
} from 'lucide-react';

type PrintMethod = 'browser' | 'websocket';
type PrintStatus = 'idle' | 'printing' | 'success' | 'error';

interface PrinterConfig {
  name: string;
  isDefault: boolean;
}

interface PrintJob {
  id: string;
  filename: string;
  method: PrintMethod;
  printer: string;
  timestamp: Date;
  status: PrintStatus;
  error?: string;
}

export const PrintTestComponent: React.FC = () => {
  // Print method configuration
  const [printMethod, setPrintMethod] = useState<PrintMethod>('browser');
  const [websocketUrl, setWebsocketUrl] = useState('ws://localhost:8080/print');
  const [websocketConnected, setWebsocketConnected] = useState(false);
  
  // Printer configuration (only used for WebSocket mode)
  const [availablePrinters, setAvailablePrinters] = useState<PrinterConfig[]>([
    { name: 'Venter på printer-liste fra service...', isDefault: false }
  ]);
  const [selectedPrinter, setSelectedPrinter] = useState('Venter på printer-liste fra service...');
  
  // PDF handling
  const [uploadedPdf, setUploadedPdf] = useState<File | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Print jobs history
  const [printJobs, setPrintJobs] = useState<PrintJob[]>([]);
  const [currentStatus, setCurrentStatus] = useState<PrintStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  
  // WebSocket connection (placeholder for future implementation)
  const wsRef = useRef<WebSocket | null>(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setUploadedPdf(file);
      
      // Create preview URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      const url = URL.createObjectURL(file);
      setPdfPreviewUrl(url);
      
      setStatusMessage(`PDF lastet opp: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
    } else {
      setStatusMessage('Vennligst velg en gyldig PDF-fil');
    }
  };

  // Connect to WebSocket (placeholder)
  const connectWebSocket = async () => {
    try {
      setStatusMessage('Kobler til lokal print service...');
      
      // TODO: Implement actual WebSocket connection
      // const ws = new WebSocket(websocketUrl);
      // 
      // ws.onopen = () => {
      //   console.log('WebSocket connected');
      //   setWebsocketConnected(true);
      //   setStatusMessage('✓ Tilkoblet lokal print service');
      //   
      //   // Request printer list
      //   ws.send(JSON.stringify({ action: 'getPrinters' }));
      // };
      // 
      // ws.onmessage = (event) => {
      //   const data = JSON.parse(event.data);
      //   
      //   if (data.type === 'printerList') {
      //     // Update available printers from service
      //     const printers = data.printers.map((p: any) => ({
      //       name: p.name,
      //       isDefault: p.isDefault
      //     }));
      //     setAvailablePrinters(printers);
      //     
      //     // Select default printer
      //     const defaultPrinter = printers.find((p: any) => p.isDefault);
      //     if (defaultPrinter) {
      //       setSelectedPrinter(defaultPrinter.name);
      //     }
      //   }
      //   
      //   if (data.type === 'printResult') {
      //     if (data.success) {
      //       setCurrentStatus('success');
      //       setStatusMessage(`✓ Utskrift vellykket: ${data.message}`);
      //     } else {
      //       setCurrentStatus('error');
      //       setStatusMessage(`✗ Utskrift feilet: ${data.error}`);
      //     }
      //   }
      // };
      // 
      // ws.onerror = (error) => {
      //   console.error('WebSocket error:', error);
      //   setWebsocketConnected(false);
      //   setStatusMessage('✗ WebSocket feil - sjekk at print service kjører');
      // };
      // 
      // ws.onclose = () => {
      //   console.log('WebSocket disconnected');
      //   setWebsocketConnected(false);
      //   setStatusMessage('Frakoblet fra print service');
      // };
      // 
      // wsRef.current = ws;
      
      // Simulated connection for now
      setTimeout(() => {
        setWebsocketConnected(true);
        setStatusMessage('✓ Tilkoblet lokal print service (simulert)');
        
        // Simulate receiving printer list (in production, this comes from the service)
        const simulatedPrinters: PrinterConfig[] = [
          { name: 'TA_UTAX 3207ci', isDefault: true },
          { name: 'TSC DA200', isDefault: false },
          { name: 'OneNote for Windows 10', isDefault: false },
          { name: 'Microsoft Print to PDF', isDefault: false }
        ];
        setAvailablePrinters(simulatedPrinters);
        setSelectedPrinter('TA_UTAX 3207ci');
        setStatusMessage('✓ Mottok printer-liste fra service (4 printere)');
      }, 1000);
      
    } catch (error) {
      setWebsocketConnected(false);
      setStatusMessage(`✗ Kunne ikke koble til: ${error}`);
    }
  };

  // Disconnect WebSocket
  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWebsocketConnected(false);
    setAvailablePrinters([{ name: 'Venter på printer-liste fra service...', isDefault: false }]);
    setSelectedPrinter('Venter på printer-liste fra service...');
    setStatusMessage('Frakoblet fra print service');
  };

  // Print using browser API
  const printWithBrowser = async () => {
    if (!uploadedPdf) {
      setStatusMessage('⚠ Vennligst last opp en PDF først');
      return;
    }

    setCurrentStatus('printing');
    setStatusMessage('Åpner print-dialog...');

    try {
      // Create an iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      // Load PDF into iframe
      iframe.src = pdfPreviewUrl!;

      // Wait for load and then print
      iframe.onload = () => {
        try {
          iframe.contentWindow?.print();
          
          // Log print job (printer name will be "Valgt i OS dialog" since we can't know)
          const job: PrintJob = {
            id: Date.now().toString(),
            filename: uploadedPdf.name,
            method: 'browser',
            printer: 'Valgt i OS dialog',
            timestamp: new Date(),
            status: 'success'
          };
          setPrintJobs(prev => [job, ...prev]);
          
          setCurrentStatus('success');
          setStatusMessage(`✓ Print-dialog åpnet. Velg printer og skriv ut.`);
          
          // Cleanup
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
          
        } catch (error) {
          setCurrentStatus('error');
          setStatusMessage(`✗ Feil ved printing: ${error}`);
          document.body.removeChild(iframe);
        }
      };

      iframe.onerror = () => {
        setCurrentStatus('error');
        setStatusMessage('✗ Kunne ikke laste PDF');
        document.body.removeChild(iframe);
      };

    } catch (error) {
      setCurrentStatus('error');
      setStatusMessage(`✗ Feil: ${error}`);
    }
  };

  // Print using WebSocket (placeholder)
  const printWithWebSocket = async () => {
    if (!uploadedPdf) {
      setStatusMessage('⚠ Vennligst last opp en PDF først');
      return;
    }

    if (!websocketConnected) {
      setStatusMessage('⚠ Ikke tilkoblet print service. Koble til først.');
      return;
    }

    setCurrentStatus('printing');
    setStatusMessage(`Sender til printer: ${selectedPrinter}...`);

    try {
      // TODO: Implement actual WebSocket communication
      // Convert PDF to base64 and send via WebSocket
      // const reader = new FileReader();
      // reader.onload = () => {
      //   const base64 = (reader.result as string).split(',')[1]; // Remove data URL prefix
      //   
      //   wsRef.current?.send(JSON.stringify({
      //     action: 'print',
      //     printer: selectedPrinter,
      //     data: base64,
      //     filename: uploadedPdf.name,
      //     copies: 1
      //   }));
      // };
      // reader.readAsDataURL(uploadedPdf);

      // Simulated print job for now
      setTimeout(() => {
        const job: PrintJob = {
          id: Date.now().toString(),
          filename: uploadedPdf.name,
          method: 'websocket',
          printer: selectedPrinter,
          timestamp: new Date(),
          status: 'success'
        };
        setPrintJobs(prev => [job, ...prev]);
        
        setCurrentStatus('success');
        setStatusMessage(`✓ Sendt til printer: ${selectedPrinter} (simulert)`);
      }, 1500);

    } catch (error) {
      setCurrentStatus('error');
      setStatusMessage(`✗ Feil ved WebSocket printing: ${error}`);
    }
  };

  // Main print handler
  const handlePrint = () => {
    if (printMethod === 'browser') {
      printWithBrowser();
    } else {
      printWithWebSocket();
    }
  };

  // Clear all print jobs
  const clearPrintJobs = () => {
    setPrintJobs([]);
    setStatusMessage('Print-historikk tømt');
  };

  // Reset status after timeout
  useEffect(() => {
    if (currentStatus === 'success' || currentStatus === 'error') {
      const timer = setTimeout(() => {
        setCurrentStatus('idle');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStatus]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Printer className="text-blue-600" size={32} />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Test Utskrift</h2>
            <p className="text-sm text-gray-600">
              Test etikettutskrift med ulike metoder og konfigurasjoner
            </p>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Om utskriftsfunksjonalitet</p>
              <p>
                Denne komponenten tester to metoder for utskrift:
              </p>
              <ul className="mt-2 space-y-1 ml-4">
                <li><strong>• Browser Print:</strong> Bruker standard window.print() - gir tilgang til ALLE dine printere via OS-dialog</li>
                <li><strong>• WebSocket Print:</strong> Sender til lokal print service for "silent" printing (krever lokal service)</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Print Method Selection */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings size={20} />
          Velg utskriftsmetode
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Browser Print */}
          <div
            onClick={() => setPrintMethod('browser')}
            className={`
              border-2 rounded-lg p-4 cursor-pointer transition-all
              ${printMethod === 'browser' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-start gap-3">
              <Monitor className={printMethod === 'browser' ? 'text-blue-600' : 'text-gray-400'} size={24} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900">Browser Print</h4>
                  {printMethod === 'browser' && (
                    <CheckCircle className="text-green-600" size={16} />
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Bruker standard browser print-dialog. Du velger printer i OS-dialogen.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    Ingen installasjon
                  </span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    Alle printere tilgjengelige
                  </span>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                    Viser dialog
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* WebSocket Print */}
          <div
            onClick={() => setPrintMethod('websocket')}
            className={`
              border-2 rounded-lg p-4 cursor-pointer transition-all
              ${printMethod === 'websocket' 
                ? 'border-purple-500 bg-purple-50' 
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-start gap-3">
              <Server className={printMethod === 'websocket' ? 'text-purple-600' : 'text-gray-400'} size={24} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900">WebSocket Print</h4>
                  {printMethod === 'websocket' && (
                    <CheckCircle className="text-green-600" size={16} />
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Sender til lokal print service for direkte utskrift. Ingen dialog.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    Silent printing
                  </span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    Pre-select printer
                  </span>
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                    Krever service
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Browser Print Info */}
        {printMethod === 'browser' && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-yellow-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Browser-begrensninger</p>
                <p className="mb-2">
                  Av sikkerhetsgrunner kan ikke browsere liste eller velge printere programmatisk.
                  Du må velge printer i OS print-dialogen som vises når du trykker "Skriv ut dokument".
                </p>
                <p className="text-xs">
                  💡 Tips: OS-dialogen gir deg tilgang til ALLE dine installerte printere (som du ser når du trykker Ctrl+P).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* WebSocket Configuration */}
        {printMethod === 'websocket' && (
          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">WebSocket Konfigurasjon</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WebSocket URL
                </label>
                <input
                  type="text"
                  value={websocketUrl}
                  onChange={(e) => setWebsocketUrl(e.target.value)}
                  placeholder="ws://localhost:8080/print"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={websocketConnected}
                />
                <p className="text-xs text-gray-500 mt-1">
                  URL til lokal print service (standard: ws://localhost:8080/print)
                </p>
              </div>

              <div className="flex gap-2">
                {!websocketConnected ? (
                  <button
                    onClick={connectWebSocket}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  >
                    <Zap size={16} />
                    Koble til service
                  </button>
                ) : (
                  <button
                    onClick={disconnectWebSocket}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Koble fra
                  </button>
                )}
                
                {websocketConnected && (
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <CheckCircle size={16} />
                    <span>Tilkoblet</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Printer Selection - Only for WebSocket mode */}
      {printMethod === 'websocket' && websocketConnected && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Printer size={20} />
            Velg printer (fra service)
          </h3>
          
          <div className="space-y-2">
            {availablePrinters.map((printer, index) => (
              <div
                key={index}
                onClick={() => setSelectedPrinter(printer.name)}
                className={`
                  border-2 rounded-lg p-3 cursor-pointer transition-all flex items-center justify-between
                  ${selectedPrinter === printer.name 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <Printer className={selectedPrinter === printer.name ? 'text-blue-600' : 'text-gray-400'} size={20} />
                  <div>
                    <span className="font-medium text-gray-900">{printer.name}</span>
                    {printer.isDefault && (
                      <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        Standard
                      </span>
                    )}
                  </div>
                </div>
                {selectedPrinter === printer.name && (
                  <CheckCircle className="text-green-600" size={20} />
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-3 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-2">
            <Info className="inline-block mr-1" size={14} />
            Printer-listen kommer fra lokal Windows Service via WebSocket
          </div>
        </Card>
      )}

      {/* PDF Upload */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Upload size={20} />
          Last opp PDF for testing
        </h3>
        
        <div className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
          >
            <FileText className="mx-auto mb-3 text-gray-400" size={48} />
            <p className="text-gray-600 mb-2">
              Klikk for å laste opp PDF, eller dra og slipp
            </p>
            <p className="text-sm text-gray-500">
              PDF-filer opp til 10MB
            </p>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            className="hidden"
          />

          {uploadedPdf && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-green-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">{uploadedPdf.name}</p>
                    <p className="text-sm text-gray-600">
                      {(uploadedPdf.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setUploadedPdf(null);
                    if (pdfPreviewUrl) {
                      URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={20} />
                </button>
              </div>
              
              {pdfPreviewUrl && (
                <div className="mt-4">
                  <button
                    onClick={() => window.open(pdfPreviewUrl, '_blank')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Eye size={16} />
                    Forhåndsvis PDF
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Print Button */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Skriv ut</h3>
        
        <div className="space-y-4">
          <button
            onClick={handlePrint}
            disabled={!uploadedPdf || currentStatus === 'printing' || (printMethod === 'websocket' && !websocketConnected)}
            className={`
              w-full py-4 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-3
              ${!uploadedPdf || currentStatus === 'printing' || (printMethod === 'websocket' && !websocketConnected)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
              }
            `}
          >
            <Printer size={24} />
            {currentStatus === 'printing' 
              ? 'Skriver ut...' 
              : printMethod === 'browser'
              ? 'Åpne print-dialog'
              : 'Send til printer'
            }
          </button>

          {printMethod === 'websocket' && !websocketConnected && (
            <div className="text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded p-3 flex items-center gap-2">
              <AlertTriangle size={16} />
              <span>Koble til WebSocket service for å bruke denne metoden</span>
            </div>
          )}

          {/* Status Message */}
          {statusMessage && (
            <div className={`
              p-4 rounded-lg border-2 flex items-center gap-3
              ${currentStatus === 'success' 
                ? 'bg-green-50 border-green-200' 
                : currentStatus === 'error'
                ? 'bg-red-50 border-red-200'
                : 'bg-blue-50 border-blue-200'
              }
            `}>
              {currentStatus === 'success' && <CheckCircle className="text-green-600" size={20} />}
              {currentStatus === 'error' && <AlertCircle className="text-red-600" size={20} />}
              {currentStatus === 'idle' && <Info className="text-blue-600" size={20} />}
              {currentStatus === 'printing' && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              )}
              <p className={`
                text-sm
                ${currentStatus === 'success' ? 'text-green-800' : ''}
                ${currentStatus === 'error' ? 'text-red-800' : ''}
                ${(currentStatus === 'idle' || currentStatus === 'printing') ? 'text-blue-800' : ''}
              `}>
                {statusMessage}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Print Jobs History */}
      {printJobs.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Print-historikk ({printJobs.length})
            </h3>
            <button
              onClick={clearPrintJobs}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
            >
              <Trash2 size={16} />
              Tøm historikk
            </button>
          </div>

          <div className="space-y-3">
            {printJobs.map((job) => (
              <div
                key={job.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="text-gray-600" size={16} />
                      <span className="font-medium text-gray-900">{job.filename}</span>
                      {job.method === 'browser' ? (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                          <Monitor size={12} />
                          Browser
                        </span>
                      ) : (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                          <Server size={12} />
                          WebSocket
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>Printer: {job.printer}</div>
                      <div>Tidspunkt: {job.timestamp.toLocaleString('no-NO')}</div>
                    </div>
                  </div>
                  <div>
                    {job.status === 'success' ? (
                      <CheckCircle className="text-green-600" size={20} />
                    ) : (
                      <AlertCircle className="text-red-600" size={20} />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Technical Information */}
      <Card className="p-6 bg-gray-50 border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Info size={20} />
          Teknisk informasjon
        </h3>
        
        <div className="space-y-4 text-sm text-gray-700">
          <div>
            <h4 className="font-semibold mb-2">Browser Print (nåværende implementasjon)</h4>
            <ul className="space-y-1 ml-4">
              <li>• Bruker window.print() API</li>
              <li>• Viser OS print dialog (kan ikke unngås av sikkerhetsgrunner)</li>
              <li>• Gir tilgang til ALLE installerte printere via OS-dialogen</li>
              <li>• Fungerer i alle moderne browsere</li>
              <li>• Ingen ekstra installasjon nødvendig</li>
              <li>• ⚠️ Browsere kan IKKE liste eller velge printere programmatisk</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">WebSocket Print (fremtidig funksjonalitet)</h4>
            <ul className="space-y-1 ml-4">
              <li>• Krever lokal Windows Service på hver warehouse PC</li>
              <li>• Servicen lytter på lokal WebSocket port (f.eks. ws://localhost:8080)</li>
              <li>• Frontend sender PDF som base64 via WebSocket</li>
              <li>• ✅ Servicen henter printer-liste fra Windows og sender til frontend</li>
              <li>• ✅ Operatør velger printer i webappen (ikke OS dialog)</li>
              <li>• Servicen printer direkte til valgt printer uten dialog</li>
              <li>• Gir "silent printing" - operatør trykker kun én knapp</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="font-medium text-yellow-900 mb-1">
              💡 Hvorfor ser du alle printere i Ctrl+P men ikke i webappen?
            </p>
            <p className="text-yellow-800">
              Dette er en browser-sikkerhetsbegrensning. Når du trykker Ctrl+P eller bruker window.print(), 
              viser OS sin egen print-dialog som har tilgang til alle printere. Men JavaScript i browseren 
              har IKKE tilgang til å liste eller velge printere. Dette er med vilje for å beskytte brukerens privatliv.
              
              For å få full printer-kontroll trenger vi en lokal Windows Service (WebSocket-løsningen).
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
